import React, { useState, useEffect } from 'react';
import {
  Shield,
  ShieldCheck,
  Lock,
  Key,
  Eye,
  Server,
  Globe,
  Users,
  Database,
  Zap,
  CheckCircle,
  AlertTriangle,
  FileText,
  Download,
  ArrowRight,
  Sparkles,
  Brain,
  Network,
  Layers,
  Activity,
  Clock,
  ShieldAlert,
  UserCheck,
  Fingerprint,
  Cpu,
  Cloud,
  Code,
  Award,
  Flag
} from 'lucide-react';

const SecurityBody = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeatureIndex((prev) => (prev + 1) % securityFeatures.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const securityPillars = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Zero-Trust Architecture',
      description: 'Every request is verified, authenticated, and authorized before accessing any resource in our orchestration layer.',
      features: ['Identity verification', 'Continuous monitoring', 'Least privilege access', 'Micro-segmentation']
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: 'End-to-End Encryption',
      description: 'All data is encrypted in transit and at rest using industry-standard AES-256 encryption across all LLM integrations.',
      features: ['AES-256 encryption', 'TLS 1.3 in transit', 'Key rotation', 'Hardware security modules']
    },
    {
      icon: <Network className="w-8 h-8" />,
      title: 'Secure Orchestration',
      description: 'Our AI orchestration layer maintains security boundaries between different LLMs, RAG systems, and knowledge hubs.',
      features: ['Isolated execution environments', 'Secure API gateways', 'Data flow validation', 'Cross-service encryption']
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: 'Comprehensive Monitoring',
      description: 'Real-time security monitoring and threat detection across all integrated AI services and data flows.',
      features: ['24/7 SOC monitoring', 'Anomaly detection', 'Audit logging', 'Incident response']
    }
  ];

  const complianceStandards = [
    { name: 'SOC 2 Type II', status: 'Certified', icon: <ShieldCheck className="w-6 h-6" /> },
    { name: 'GDPR', status: 'Compliant', icon: <Globe className="w-6 h-6" /> },
    { name: 'CCPA', status: 'Compliant', icon: <Users className="w-6 h-6" /> },
    { name: 'HIPAA Ready', status: 'Available', icon: <FileText className="w-6 h-6" /> },
    { name: 'ISO 27001', status: 'In Progress', icon: <Award className="w-6 h-6" /> },
    { name: 'FedRAMP', status: 'Planned', icon: <Flag className="w-6 h-6" /> }
  ];

  const securityFeatures = [
    {
      category: 'Identity & Access',
      icon: <UserCheck className="w-6 h-6" />,
      items: [
        'Single Sign-On (SSO) with SAML 2.0',
        'Multi-Factor Authentication (MFA)',
        'Role-Based Access Control (RBAC)',
        'Just-in-Time (JIT) provisioning',
        'API key management',
        'Session management'
      ]
    },
    {
      category: 'Data Protection',
      icon: <Database className="w-6 h-6" />,
      items: [
        'Data classification and labeling',
        'Automatic data masking',
        'PII detection and redaction',
        'Data residency controls',
        'Backup encryption',
        'Data retention policies'
      ]
    },
    {
      category: 'Infrastructure Security',
      icon: <Server className="w-6 h-6" />,
      items: [
        'VPC isolation and segmentation',
        'Web Application Firewall (WAF)',
        'DDoS protection',
        'Intrusion detection systems',
        'Container security scanning',
        'Infrastructure as Code (IaC)'
      ]
    },
    {
      category: 'AI Security',
      icon: <Brain className="w-6 h-6" />,
      items: [
        'Model poisoning protection',
        'Prompt injection safeguards',
        'Output filtering and validation',
        'LLM usage monitoring',
        'Bias detection and mitigation',
        'Adversarial attack prevention'
      ]
    }
  ];

  const architectureComponents = [
    {
      name: 'API Gateway',
      description: 'Secure entry point with rate limiting, authentication, and request validation',
      security: ['JWT validation', 'Rate limiting', 'Request sanitization']
    },
    {
      name: 'Orchestration Engine',
      description: 'Core engine that securely routes requests between LLMs and knowledge systems',
      security: ['Service mesh encryption', 'Circuit breakers', 'Audit logging']
    },
    {
      name: 'Knowledge Hub',
      description: 'Encrypted storage for RAG systems with fine-grained access controls',
      security: ['Encryption at rest', 'Access policies', 'Data lineage tracking']
    },
    {
      name: 'LLM Connectors',
      description: 'Secure integrations with multiple LLM providers maintaining isolation',
      security: ['Provider key management', 'Response validation', 'Traffic encryption']
    }
  ];

  const trustMetrics = [
    { metric: '99.9%', label: 'Security Uptime', icon: <Activity className="w-5 h-5" /> },
    { metric: '<15min', label: 'Incident Response', icon: <Clock className="w-5 h-5" /> },
    { metric: '0', label: 'Data Breaches', icon: <ShieldCheck className="w-5 h-5" /> },
    { metric: '24/7', label: 'Security Monitoring', icon: <Eye className="w-5 h-5" /> }
  ];

  return (
    <main className="bg-black text-white font-sans">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(16, 185, 129, 0.15), transparent 40%)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/20 via-transparent to-blue-900/20" />
      </div>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-green-600/20 to-blue-600/20 border border-green-500/30 rounded-full px-4 py-2 mb-6">
              <Shield className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-300">Enterprise Security</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-green-200 to-blue-200 bg-clip-text text-transparent">
              Security-First
              <span className="block mt-2">AI Orchestration</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-12">
              Built from the ground up with enterprise security at its core, SelficAI provides a secure orchestration layer 
              for LLMs, RAG systems, and knowledge hubs. Trust us to protect your most sensitive data while delivering 
              powerful AI automation capabilities.
            </p>

            {/* Trust Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {trustMetrics.map((item, index) => (
                <div key={index} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center">
                  <div className="text-green-400 mb-2 flex justify-center">
                    {item.icon}
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-white mb-1">{item.metric}</div>
                  <div className="text-sm text-gray-400">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Security Pillars */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent">
              Security Foundations
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our multi-layered security approach protects your AI workflows across every component of our orchestration platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {securityPillars.map((pillar, index) => (
              <div key={index} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-8 hover:border-green-500/50 transition-all duration-300">
                <div className="text-green-400 mb-4">
                  {pillar.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{pillar.title}</h3>
                <p className="text-gray-300 mb-6 leading-relaxed">{pillar.description}</p>
                <div className="space-y-2">
                  {pillar.features.map((feature, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-sm text-gray-400">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Architecture */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent">
              Secure AI Orchestration Architecture
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Every component in our architecture is designed with security and isolation in mind.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {architectureComponents.map((component, index) => (
              <div key={index} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 hover:border-green-500/50 transition-all duration-300">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-500 rounded-xl mx-auto mb-3 flex items-center justify-center">
                    <Layers className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-white">{component.name}</h3>
                </div>
                <p className="text-sm text-gray-400 mb-4 leading-relaxed">{component.description}</p>
                <div className="space-y-1">
                  {component.security.map((item, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-400 rounded-full flex-shrink-0" />
                      <span className="text-xs text-gray-500">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Architecture Diagram */}
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-8">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-white mb-2">Data Flow Security</h3>
              <p className="text-sm text-gray-400">End-to-end encryption and validation at every step</p>
            </div>
            <div className="flex items-center justify-center space-x-4 flex-wrap">
              <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-3 py-2">
                <Users className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-white">User Request</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-3 py-2">
                <Shield className="w-4 h-4 text-green-400" />
                <span className="text-sm text-white">API Gateway</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-3 py-2">
                <Cpu className="w-4 h-4 text-purple-400" />
                <span className="text-sm text-white">Orchestration</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-3 py-2">
                <Brain className="w-4 h-4 text-orange-400" />
                <span className="text-sm text-white">LLM/RAG</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Features Grid */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent">
              Comprehensive Security Controls
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Granular security controls across every aspect of AI orchestration and data handling.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {securityFeatures.map((category, index) => (
              <div 
                key={index} 
                className={`bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 transition-all duration-300 ${
                  activeFeatureIndex === index ? 'border-green-500/50 scale-105' : 'hover:border-green-500/30'
                }`}
              >
                <div className="text-green-400 mb-4 flex items-center space-x-2">
                  {category.icon}
                  <h3 className="font-semibold text-white">{category.category}</h3>
                </div>
                <div className="space-y-2">
                  {category.items.map((item, i) => (
                    <div key={i} className="flex items-start space-x-2">
                      <CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-gray-400 leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent">
              Compliance & Certifications
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Meeting the highest industry standards for security, privacy, and regulatory compliance.
            </p>
          </div>
{/* 
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {complianceStandards.map((standard, index) => (
              <div key={index} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center hover:border-green-500/50 transition-all duration-300">
                <div className="text-green-400 mb-3 flex justify-center">
                  {standard.icon}
                </div>
                <h3 className="font-semibold text-white mb-2">{standard.name}</h3>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                  standard.status === 'Certified' ? 'bg-green-500/20 text-green-300' :
                  standard.status === 'Compliant' ? 'bg-blue-500/20 text-blue-300' :
                  standard.status === 'Available' ? 'bg-purple-500/20 text-purple-300' :
                  'bg-yellow-500/20 text-yellow-300'
                }`}>
                  {standard.status}
                </span>
              </div>
            ))}
          </div> */}
        </div>
      </section>

      {/* Security Resources */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent">
              Security Documentation & Resources
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 hover:border-green-500/50 transition-all duration-300">
              <FileText className="w-8 h-8 text-green-400 mb-4" />
              <h3 className="font-semibold text-white mb-2">Security Whitepaper</h3>
              <p className="text-gray-400 text-sm mb-4">Detailed overview of our security architecture, controls, and best practices.</p>
              <button className="flex items-center space-x-2 text-green-400 hover:text-green-300 transition-colors">
                <Download className="w-4 h-4" />
                <span className="text-sm">Download PDF</span>
              </button>
            </div>

            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 hover:border-green-500/50 transition-all duration-300">
              <ShieldCheck className="w-8 h-8 text-green-400 mb-4" />
              <h3 className="font-semibold text-white mb-2">Compliance Reports</h3>
              <p className="text-gray-400 text-sm mb-4">Access our SOC 2 Type II report and other compliance documentation.</p>
              <button className="flex items-center space-x-2 text-green-400 hover:text-green-300 transition-colors">
                <Eye className="w-4 h-4" />
                <span className="text-sm">View Reports</span>
              </button>
            </div>

            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 hover:border-green-500/50 transition-all duration-300">
              <Code className="w-8 h-8 text-green-400 mb-4" />
              <h3 className="font-semibold text-white mb-2">Security Best Practices</h3>
              <p className="text-gray-400 text-sm mb-4">Implementation guides and security recommendations for developers.</p>
              <button className="flex items-center space-x-2 text-green-400 hover:text-green-300 transition-colors">
                <ArrowRight className="w-4 h-4" />
                <span className="text-sm">Learn More</span>
              </button>
            </div>

            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 hover:border-green-500/50 transition-all duration-300">
              <AlertTriangle className="w-8 h-8 text-green-400 mb-4" />
              <h3 className="font-semibold text-white mb-2">Incident Response</h3>
              <p className="text-gray-400 text-sm mb-4">Our commitment to transparency and rapid incident response procedures.</p>
              <button className="flex items-center space-x-2 text-green-400 hover:text-green-300 transition-colors">
                <FileText className="w-4 h-4" />
                <span className="text-sm">View Policy</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-green-600/20 to-blue-600/20 backdrop-blur-xl border border-green-500/30 rounded-3xl p-12">
            <Shield className="w-12 h-12 text-green-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent">
              Security You Can Trust
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Ready to experience enterprise-grade security with powerful AI orchestration? 
              Our security team is here to answer your questions and ensure your data stays protected.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-green-700 hover:to-blue-700 transition-all duration-300 flex items-center justify-center space-x-2">
                <span>Schedule Security Review</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button className="border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                Contact Security Team
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SecurityBody;